import type { ComponentProps } from "react";
import { ROUTES } from "@routing/routes";
import { VENDOR_KEY, isVendorId } from "@customer/Search/useSearchFilters";
import { isVendorSlug } from "@customer/_helpers/vendorSlug";
import type { HomePromotion } from "@models/promotion";
import type { PromoBanner } from "../components/PromoBanner";

// The button text for a promotion that links somewhere but names no label of
// its own (PO ruling 2026-10-05, the old mock's hero copy).
const PROMO_DEFAULT_CTA_LABEL = "Shop now";

// The product grid filtered to one vendor, in the URL form useSearchFilters
// reads (?vendor=<id>, the key the filter panel writes).
const vendorGridHref = (vendorId: number): string =>
    `${ROUTES.SEARCH}?${new URLSearchParams({ [VENDOR_KEY]: String(vendorId) })}`;

// The API's own rule for ctaHref, mirrored (PO ruling 2026-10-05: in-app
// links only): no whitespace, control character or backslash anywhere — the
// URL parser strips tab/CR/LF, so "/\t/evil.com" would become "//evil.com" —
// then one leading "/" not followed by another (that would be another host).
const FORBIDDEN_IN_HREF = /[\s\p{Cc}\\]/u;
const isAppPath = (href: string): boolean =>
    !FORBIDDEN_IN_HREF.test(href) &&
    href.startsWith("/") &&
    !href.startsWith("//");

// The path part of an app href: before any query or hash, without a trailing
// slash (so "/c/browse/" is the browse home too).
const pathOf = (href: string): string => {
    const path = href.split(/[?#]/, 1)[0];
    return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
};

// Where a promotion's button goes (DecisionLog §4.14, 2026-10-03): an explicit
// ctaHref always wins; else a vendor promotion goes to its vendor's page
// (2026-10-23, #425), or to its vendor's grid when the promotion carries no
// slug the page would accept (an API from before #425); a house promotion with
// no link has nowhere to go, so no button. Every button must also be a real
// way somewhere:
//   - in-app only — an href that is not an app path gets no button, even on a
//     vendor promotion (the admin asked for that link, not the default);
//   - never ROUTES.BROWSE — every CTA on the browse home must leave the page
//     it is on (#401), so a link back to it gets no button;
//   - a vendor id useSearchFilters would not read back (0, negative,
//     fractional) gets no button rather than a link to an unfiltered page.
const resolvePromoHref = (promo: HomePromotion): string | undefined => {
    if (promo.ctaHref) {
        return isAppPath(promo.ctaHref) &&
            pathOf(promo.ctaHref) !== ROUTES.BROWSE
            ? promo.ctaHref
            : undefined;
    }
    if (promo.vendorSlug !== null && isVendorSlug(promo.vendorSlug)) {
        return ROUTES.VENDOR(promo.vendorSlug);
    }
    if (promo.vendorId !== null && isVendorId(promo.vendorId)) {
        return vendorGridHref(promo.vendorId);
    }
    return undefined;
};

// The props HeroBanner and PromoBanner take, from one API promotion. A button
// label is only given when there is a link to put it on.
export const toPromoBannerProps = (
    promo: HomePromotion,
): ComponentProps<typeof PromoBanner> => {
    const ctaHref = resolvePromoHref(promo);
    return {
        label: promo.label,
        imgSrc: promo.imageUrl ?? undefined,
        ctaHref,
        ctaLabel: ctaHref
            ? promo.ctaLabel || PROMO_DEFAULT_CTA_LABEL
            : undefined,
    };
};
